import React from "react";
import Autocomplete from "@mui/material/Autocomplete";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import SearchIcon from "@mui/icons-material/Search";
import { Typography, TextField, IconButton, Grid } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import parse from "autosuggest-highlight/parse";
import throttle from "lodash/throttle";
import { useSelector, useDispatch } from "react-redux";
import { setLocation } from "../redux/actions";
import { useNavigate } from "react-router-dom";

interface PlaceOption {
  description: string;
  structured_formatting: {
    main_text: string;
    main_text_matched_substrings: { offset: number; length: number }[];
    secondary_text: string;
  };
}

function loadScript(src: string, position: HTMLElement | null, id: string) {
  if (!position) {
    return;
  }

  const script = document.createElement("script");
  script.setAttribute("async", "");
  script.setAttribute("id", id);
  script.src = src;
  position.appendChild(script);
}

const autocompleteService = { current: null };

const useStyles = makeStyles((theme) => ({
  icon: {
    color: theme.palette.text.secondary,
    marginRight: theme.spacing(2),
  },
}));

interface IProps {
  // any other props that come into the component
}

interface PlaceType {
  place_id: any;
  description: string;
  value: any;
  structured_formatting: {
    main_text: string;
    secondary_text: string;
    main_text_matched_substrings: [
      {
        offset: number;
        length: number;
      }
    ];
  };
}

function GoogleMaps({ ...props }: IProps) {
  const navigate = useNavigate();
  const stateR: any = useSelector((state) => state);
  const classes = useStyles();
  const dispatch = useDispatch();
  const [value, setValue] = React.useState<any>(null);
  const [inputValue, setInputValue] = React.useState("");
  const [options, setOptions] = React.useState<PlaceType[]>([]);
  const loaded = React.useRef(false);

  const onSubmit = () => {
    const location = value.location;
    dispatch(setLocation(value));
    navigate(`/forecast?${value.description}&lat=${value.location.lat}&lng=${value.location.lng}`);
  };

  if (typeof window !== "undefined" && !loaded.current) {
    if (!document.querySelector("#google-maps")) {
      loadScript(
        "https://maps.googleapis.com/maps/api/js?key=AIzaSyBiqwQyj5lnCfRD-LDm2OI2VmNr_rd-XwA&libraries=places",
        document.querySelector("head"),
        "google-maps"
      );
    }

    loaded.current = true;
  }

  const fetch = React.useMemo(
    () =>
      throttle(
        (
          request: { input: string },
          callback: (results?: PlaceType[]) => void
        ) => {
          (autocompleteService.current as any).getPlacePredictions(
            request,
            callback
          );
        },
        200
      ),
    []
  );

  React.useEffect(() => {
    let active = true;

    if (!autocompleteService.current && (window as any).google) {
      autocompleteService.current = new (
        window as any
      ).google.maps.places.AutocompleteService();
    }

    if (!autocompleteService.current) {
      return undefined;
    }

    if (inputValue === "") {
      setOptions(value ? [value] : []);
      return undefined;
    }

    fetch({ input: inputValue }, (results?: PlaceType[]) => {
      if (active) {
        let newOptions = [] as PlaceType[];

        if (value) {
          newOptions = [value];
        }

        if (results) {
          newOptions = [...newOptions, ...results];
        }

        setOptions(newOptions);
      }
    });

    return () => {
      active = false;
    };
  }, [value, inputValue, fetch]);

  return (
    <Autocomplete
      forcePopupIcon={false}
      className="inline-flex"
      size="small"
      id="google-map-demo"
      style={{ width: 300 }}

      getOptionLabel={(option) =>
        typeof option === "string" ? option : option.description
      }

      filterOptions={(x) => x}
      options={options}
      autoComplete
      includeInputInList
      filterSelectedOptions
      value={value}

      onChange={(event: any, newValue: PlaceType | null) => {
        if (!newValue) return;

        const service = new (window as any).google.maps.places.PlacesService(
          document.createElement("div")
        );

        service.getDetails({ placeId: newValue.place_id }, (place: any, status: any) => {
          if (status === (window as any).google.maps.places.PlacesServiceStatus.OK && place.geometry) {
            const location = {
              lat: place.geometry.location.lat(),
              lng: place.geometry.location.lng(),
            };

            const updatedValue = {
              ...newValue,
              location,
            };

            setOptions([updatedValue, ...options]);
            setValue(updatedValue);
            dispatch(setLocation(updatedValue));
          }
        });
      }}

      onInputChange={(event, newInputValue) => {
        setInputValue(newInputValue);
      }}

      renderInput={(params) => (
        <div className="relative w-full">
          <div className="absolute right-8 z-10">
            <IconButton className="p-2" onClick={onSubmit} disabled={!value}>
              <SearchIcon />
            </IconButton>
          </div>
          <TextField
            {...params}
            color="secondary"
            label="Enter your town/city"
            variant="outlined"
            fullWidth
          />
        </div>
      )}

      renderOption={(props, option) => {
        const { key, ...rest } = props;

        const matches = option.structured_formatting.main_text_matched_substrings;
        const parts = parse(
          option.structured_formatting.main_text,
          matches.map((match: { offset: number; length: number }) => [
            match.offset,
            match.offset + match.length,
          ])
        );

        return (
          <li key={key} {...rest}>
            <Grid container alignItems="center">
              <Grid item>
                <LocationOnIcon className={classes.icon} />
              </Grid>
              <Grid item xs>
                {parts.map((part, index) => (
                  <span key={index} style={{ fontWeight: part.highlight ? 700 : 400 }}>
                    {part.text}
                  </span>
                ))}
                <Typography variant="body2" color="textSecondary">
                  {option.structured_formatting.secondary_text}
                </Typography>
              </Grid>
            </Grid>
          </li>
        );
      }}

    />
  );
}

export default GoogleMaps;
